import { useInterpret, useSelector as useXStateSelector } from '@xstate/react';
import { StateFrom } from 'xstate';
import { useSelector } from 'react-redux';
import noOp from 'utils/noop';
import { ApiMethod } from 'services/api';
import { selectCampaign } from 'modules/campaignAnalytics/selectors';
import {
  selectWysiwigEditorEnabled,
  selectIsRokuTemplatingFeatureFlagOn,
} from 'core/userContext/selectors';
import { useRestMutation } from 'utils/hooks/api/useRestMutation';
import { useCloneCampaign } from 'modules/ai/hooks/campaigns/useCloneCampaign';
import { GraphQLCampaign } from 'modules/campaigns/lib/types';
import { generateUpdateCampaignPayload } from 'modules/campaigns/lib/hooks/useGenerateSaveCampaignPayload/utils';
import {
  UpdateCampaignPayload,
  UpdatedCampaignFields,
  UPSERT_CAMPAIGN_API_URL,
  useGenerateSaveCampaignPayload,
  useGenerateSaveTemplatePayload,
  generateUpdateTemplatePayload,
} from 'modules/campaigns/lib/hooks';
import { useQueryCampaignById } from 'modules/campaigns/editor/hooks';
import {
  CloneAndUpdateStates,
  CloneAndUpdateMachine,
  createCloneAndUpdateMachineEffects,
} from 'modules/ai/nba/hooks/useCreateCloneCampaignAndTemplate.state';
import { removeLocale } from 'api/TemplateIndexAPI';
import { useCreateNBAAudienceList } from 'modules/ai/nba/hooks/useCreateNBAAudienceList';
import { useUpdateNBAService } from 'modules/ai/nba/hooks/useUpdateNBAService';
import { useLazyDeleteListMutation } from 'modules/listIndex/hooks';
import { Suggestion, NBAContextState } from 'modules/ai/nba/types';
import { useTemplateSingleContentEditById } from 'modules/templateEditor/hooks/useTemplateSingleContentEditById';
import { useUpdateTemplateMedium } from 'modules/ai/nba/hooks/useUpdateTemplateMedium';
import { createTransformTemplate } from 'modules/ai/nba/utils/transformTemplate';
import { useUpdateDefaultLocale } from 'modules/templateEditor/hooks/useUpdateDefaultLocale';

/**
@abstract (GraphQLCampaign, Partial<UpdatedCampaignFields>) -> UpdateCampaignPayload
*/
export type CampaignUpdater = {
  (campaign: GraphQLCampaign, updateFields: Partial<UpdatedCampaignFields>): UpdateCampaignPayload;
};

/**
@abstract CampaignUpdater -> (GraphQLCampaign, Number) -> UpdateCampaignPayload)
*/
export interface UpdateCampaignFields {
  (updator: CampaignUpdater): (campaign: GraphQLCampaign, listId: number) => UpdateCampaignPayload;
}

export const useCreateCloneCampaignAndTemplate = (
  campaignId: number,
  updateFields: UpdateCampaignFields,
  templateLocaleData: NBAContextState['templateLocaleData'],
  suggestionContent: Suggestion,
) => {
  const campaignName = useSelector(selectCampaign)?.name;
  const wysiwygEditorEnabled = useSelector(selectWysiwigEditorEnabled) ?? false;
  const IsRokuTemplatingFeatureFlagOn = useSelector(selectIsRokuTemplatingFeatureFlagOn);
  const { mutateAsync: mutateList } = useCreateNBAAudienceList();
  const { mutateAsync: updateNBAService } = useUpdateNBAService();
  const { deleteListAsync } = useLazyDeleteListMutation();
  const { refetch: cloneCampaignMutation } = useCloneCampaign(campaignId);
  const { refetch: fetchCampaign } = useQueryCampaignById({ skip: true });
  const { refetch: fetchTemplate } = useTemplateSingleContentEditById({
    skip: true,
  });
  const [updateDefaultLocale] = useUpdateDefaultLocale({
    onCompleted: noOp,
    onError: noOp,
    onSuccess: noOp,
  });

  const { mutateAsync: mutateCampaign } = useRestMutation(UPSERT_CAMPAIGN_API_URL, ApiMethod.post);
  const generateCampaignPayload = useGenerateSaveCampaignPayload(generateUpdateCampaignPayload);
  const generateTemplatePayload = useGenerateSaveTemplatePayload(generateUpdateTemplatePayload);
  const getTemplateUpdater = useUpdateTemplateMedium();
  const transformTemplate = createTransformTemplate(
    generateTemplatePayload,
    suggestionContent.suggestionText,
    wysiwygEditorEnabled,
    IsRokuTemplatingFeatureFlagOn,
    templateLocaleData,
  );

  const service = useInterpret(
    CloneAndUpdateMachine,
    createCloneAndUpdateMachineEffects({
      applyCampaignDataTransformer: updateFields(generateCampaignPayload),
      applyTemplateDataTransformer: transformTemplate,
      cloneCampaignMutation,
      createAudienceList: () => mutateList({ name: campaignName }),
      updateNBAService: (listId: number) => updateNBAService({ campaignId, listId }),
      deleteAudienceList: deleteListAsync,
      fetchCampaign,
      getTemplateUpdater,
      fetchTemplate,
      onRedirect: (campaignPath: string) => {
        window.location.href = campaignPath;
      },
      saveCampaignData: mutateCampaign,
      templateLocaleData,
      removeLocale,
      updateDefaultLocale,
    }),
  );

  const isDataReady = useXStateSelector(service, (state: StateFrom<typeof CloneAndUpdateMachine>) =>
    state.matches(CloneAndUpdateStates.ready),
  );

  const isError = useXStateSelector(service, (state: StateFrom<typeof CloneAndUpdateMachine>) =>
    state.matches(CloneAndUpdateStates.error),
  );

  const isLoading = useXStateSelector(
    service,
    (state: StateFrom<typeof CloneAndUpdateMachine>) =>
      ![CloneAndUpdateStates.inactive, CloneAndUpdateStates.error].some(state.matches),
  );

  const cloneCampaign = () => {
    if (!campaignId || Number.isNaN(campaignId)) {
      service.send(CloneAndUpdateStates.error);
    } else {
      service.send(CloneAndUpdateStates.createList);
    }
  };
  const resetState = () => {
    service.send(CloneAndUpdateStates.inactive);
  };

  return {
    cloneCampaign,
    isDataReady,
    isLoading,
    isError,
    resetState,
  };
};
